// Lotes (grupos de manejo). Ganadería → Lotes.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  Campo, Lote, Modal, PROPOSITOS, Predio, Proposito, SortTh, Switch, fieldCls, ganRpc, propositoLabel, useSort,
} from './shared';

const vacio = { id: '', predio_id: '', nombre: '', proposito: 'mixto' as Proposito, activo: true };

export function Lotes() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: lotes = [], isFetching } = useQuery<Lote[]>({
    queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar'),
  });
  const { data: predios = [] } = useQuery<Predio[]>({
    queryKey: ['gan-predios'], queryFn: () => ganRpc<Predio[]>('gan_predio_listar'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (l?: Lote) => {
    if (l) {
      setEditId(l.id);
      setForm({ id: l.id, predio_id: l.predio_id, nombre: l.nombre, proposito: l.proposito, activo: l.activo });
    } else { setEditId(null); setForm({ ...vacio, predio_id: predios[0]?.id ?? '' }); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => ganRpc<Lote>('gan_lote_guardar', {
      _data: { id: editId || undefined, predio_id: form.predio_id, nombre: form.nombre, proposito: form.proposito, activo: form.activo },
    }),
    onSuccess: () => { toast.success('Lote guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['gan-lotes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => ganRpc('gan_lote_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Lote eliminado'); void qc.invalidateQueries({ queryKey: ['gan-lotes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: Lote, key: string): string | number | null => {
    switch (key) {
      case 'predio': return r.predio_nombre;
      case 'nombre': return r.nombre;
      case 'proposito': return propositoLabel(r.proposito);
      default: return null;
    }
  }, []);
  const sort = useSort(lotes, getVal);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5" /> Lotes</h1>
        <Button onClick={() => abrir()} disabled={!predios.length}><Plus className="w-4 h-4 mr-1" /> Nuevo lote</Button>
      </div>
      {!predios.length && (
        <p className="text-sm text-muted-foreground">Crea primero un predio en «Predios y potreros» para poder registrar sus lotes.</p>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Lotes ({lotes.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="predio" sort={sort}>Predio</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="proposito" sort={sort}>Propósito</SortTh>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin lotes registrados</td></tr>}
              {sort.sorted.map((l, i) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{l.predio_nombre}</td>
                  <td className="py-2 pr-3 font-medium">{l.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{propositoLabel(l.proposito)}</span></td>
                  <td className="py-2 pr-3">{l.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(l)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${l.nombre}"?`, { peligro: true })) eliminar.mutate(l.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar lote' : 'Nuevo lote'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Predio *">
              <select className={fieldCls} value={form.predio_id} onChange={(e) => setForm((f) => ({ ...f, predio_id: e.target.value }))}>
                <option value="">— Selecciona —</option>
                {predios.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></Campo>
            <Campo label="Propósito">
              <select className={fieldCls} value={form.proposito} onChange={(e) => setForm((f) => ({ ...f, proposito: e.target.value as Proposito }))}>
                {PROPOSITOS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </Campo>
            <Switch checked={form.activo} onChange={(v) => setForm((f) => ({ ...f, activo: v }))} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.predio_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
